<template>
  <div>
    <router-view></router-view>
    <index-card :loading="loading">
      <template slot="header">
        <div class="d-flex align-center">
          <v-btn text icon large to="/" exact>
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditEmployee"
            color="white"
            class="blue-grey--text elavation-0 font-weight-bold"
            @click="$router.push({ name: 'CreateEmployee' })"
            center
          >
            <v-icon small left>mdi-plus</v-icon>Adicionar Funcionário
          </v-btn>
        </div>
        <div class="d-flex align-center headline white--text">
          Funcionários
          <v-btn text icon color="white" aria-label="Atualizar lista" @click="getData()">
            <v-icon class="white--text">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </template>

      <template slot="subheader">
        <div class="index-employees-filters">
          <filter-data
            :with_client="false"
            :with_date_start="false"
            :with_date_end="false"
            with_text_search
            text_search_label="Pesquise.."
            :extra-filters-count="3"
            :loading_block="loading"
            @doneFilter="handleDoneFilter"
          >
            <template v-slot:extra-filters>
              <v-col cols="12" md="6">
                <v-select
                  v-model="team_filter"
                  :items="teamOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-account-group"
                  label="Time"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="role_filter"
                  :items="roleOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-badge-account"
                  label="Cargo"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="status_filter"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-list-status"
                  label="Status"
                />
              </v-col>
            </template>
          </filter-data>
        </div>
      </template>

      <template slot="data_table">
        <v-data-table
          :headers="headers"
          :items="filteredEmployees"
          :search="tableSearch"
          :loading="loading"
          class="elevation-4"
          style="max-height: 75vh; overflow-y: auto"
          mobile-breakpoint="1"
          @click:row="editItem"
        >
          <template v-slot:no-data>
            <v-alert :value="true" type="error">
              Não possui funcionários cadastrados
            </v-alert>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" type="error" icon="mdi-alert">
              Não foi possível encontrar "{{ tableSearch }}" nos funcionários.
            </v-alert>
          </template>
        </v-data-table>
      </template>
    </index-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Api from '@/api/index'

const TEAM_LABELS = {
  none: 'Sem time',
  metropolitana: 'Metropolitana',
  expansao_minas: 'Expansão Minas',
  triangulo: 'Triangulo',
}

const DISABLED_STATUS_ID = 2

export default {
  name: 'IndexEmployee',
  components: {
    IndexCard: () => import('@/_backoffice/_registrations/components/shared/index/IndexCard.vue'),
    FilterData: () => import('@/components/shared/FilterData.vue'),
  },
  data() {
    return {
      loading: false,
      employees: [],
      local_filter: {},
      team_filter: 'all',
      role_filter: 'all',
      status_filter: 'active',
      teamOptions: [
        { text: 'Todos', value: 'all' },
        { text: 'Sem time', value: 'none' },
        { text: 'Metropolitana', value: 'metropolitana' },
        { text: 'Expansão Minas', value: 'expansao_minas' },
        { text: 'Triangulo', value: 'triangulo' },
      ],
      roleOptions: [
        { text: 'Todos', value: 'all' },
        { text: 'Vendedor', value: 'Vendedor' },
        { text: 'Financeiro', value: 'Financeiro' },
        { text: 'Diretor', value: 'Diretor' },
      ],
      statusOptions: [
        { text: 'Ativos', value: 'active' },
        { text: 'Desativados', value: 'disabled' },
      ],
      headers: [
        { text: 'Nome', value: 'attributes.name', align: 'center' },
        { text: 'CPF', value: 'attributes.cpf', align: 'center' },
        { text: 'Cargo', value: 'role_label', align: 'center' },
        { text: 'Time', value: 'team_label', align: 'center' },
      ],
    }
  },
  computed: {
    canEditEmployee() {
      return this.isDirector() || this.isSalesExecutive()
    },
    tableSearch() {
      return this.local_filter?.query ? String(this.local_filter.query) : ''
    },
    filteredEmployees() {
      if (!Array.isArray(this.employees)) return []

      let items = this.employees.map((employee) => ({
        ...employee,
        role_label: this.roleLabel(employee),
        team_label: this.teamLabel(employee),
      }))

      if (this.role_filter && this.role_filter !== 'all') {
        const roleFilter = String(this.role_filter).toLowerCase()
        items = items.filter((employee) =>
          String(employee.role_label).toLowerCase().includes(roleFilter)
        )
      }

      return items
    },
    ...mapState({
      activeModalityId: (state) => state.App.activeModalityId,
    }),
  },
  methods: {
    getData() {
      this.getEmployees()
    },
    handleDoneFilter(filter = {}) {
      this.local_filter = { ...filter }
      this.getEmployees()
    },
    teamLabel(item) {
      const attrs = item?.attributes || {}
      return attrs.team_translated || TEAM_LABELS[attrs.team] || 'Sem time'
    },
    roleLabel(item) {
      return item?.attributes?.roles_translated
        || item?.attributes?.role?.data?.[0]?.attributes?.translated
        || '-'
    },
    buildIndexParams() {
      const params = {
        modality_ids: [this.activeModalityId],
      }

      if (this.team_filter && this.team_filter !== 'all') {
        params.teams = [this.team_filter]
      }

      if (this.status_filter === 'disabled') {
        params.status_ids = [DISABLED_STATUS_ID]
      }

      return params
    },
    getEmployees() {
      if (!this.activeModalityId) return

      this.loading = true
      Api.Employee.index(this.buildIndexParams())
        .then((response) => response.data)
        .then((payload) => {
          this.employees = Array.isArray(payload?.data) ? payload.data : []
        })
        .catch((error) => {
          console.log(error)
          this.employees = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    editItem(item) {
      if (this.canEditEmployee && item?.id) {
        this.$router.push({ name: 'ShowEmployee', params: { id: item.id } })
      }
    },
  },
  watch: {
    activeModalityId(nv) {
      if (nv) this.getEmployees()
    },
  },
}
</script>

<style scoped>
.index-employees-filters {
  width: 100%;
  flex: 1 1 100%;
  min-width: 0;
}
.index-employees-filters >>> .filter-data-card {
  width: 100%;
}
</style>
